import { setLanguageBundle as setAndCacheLanguageBundle } from '@nackle/intl-tools';
import { DEFAULT_LOCALE_CODE } from '../../const';
import { Endpoint } from '../../endpoints/endpoints';
import { AUTH_ENV, CMX_ENV } from '../../environments';
import { FetchAssistant } from '../../utils';
import { EventSystem } from '../../utils/eventSystem';
import { authService } from '../authService';

const S3_TRANLSATIONS_ENDPOINT = `https://learnlxp-public-assets.com/hoya/${ AUTH_ENV }/site-bundle.json`; 

/**
 * A controller used to interact with the cache in the `intl-tools`
 * package.
 */
export class TranslationController {

    events = {
        SUCCESS: 'success',
        FAIL: 'fail',
        UPDATE: 'update',
        MANUAL_UPDATE: 'manual_update',
    };

    /**
     * @param { { projectKey: string, localTranslationBundles: object, attemptFetch: boolean } } 
     */
    constructor( { projectKey = undefined, localTranslationBundles = undefined, attemptFetch = false } = {} ) {
        this.projectKey = projectKey;
        this.localeCode = DEFAULT_LOCALE_CODE;
        this.localTranslationBundles = localTranslationBundles;
        this.languageBundle = undefined;
        this._eventSystem = new EventSystem();
        this.subscriberList = [];

        if ( attemptFetch ) {
            this.fetchTranslations( { locale: DEFAULT_LOCALE_CODE } );
        }

        // Create a fetch assistant with a middleware function that passes in the user's
        // access token if available. This is meant to make any potentially necessary
        // fetch requests easier in the context of CMX translations.
        this._fetchAssistant = new FetchAssistant( 'translations', [ ( _endpoint, options ) => {
            options.headers = { ...( options.headers || {} ) };
            const bearerToken = authService.getAccessToken();
            if ( bearerToken ) {
                options.headers.authorization = `Bearer ${ bearerToken }`;
            }
        } ] );
    }

    follow( fn ) {
        this.subscriberList.push( fn );
    }

    unfollow( fn ) {
        this.subscriberList = this.subscriberList.filter( x => x !== fn );
    }

    notify( locale ) {
        this.subscriberList.forEach( fn => fn?.( locale ) );
    }

    async getLanguageBundle( accessToken ) {
        try {
            const response = await fetch( 
                S3_TRANLSATIONS_ENDPOINT, 
                {   
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${ accessToken }`,
                        'content-type': 'application/json',
                        'Accept': 'application/json'
                    } 
                }
            );
            
            // Get the response's data
            const responseData = await response.json();
            const translations = responseData.find( d => d.projectName == 'hoya-learning' );
         
            // Get what we need from the response
            const bundles = translations?.bundles;
            const locales = Object.keys( bundles );
                
            if ( bundles ) {
                // Return success condition
                return {
                    data: {
                        bundles,
                        locales
                    },
                    success: true,
                    errorMessage: ''
                }; 
            } else {
                console.error( 'Error loading CMX translations for ', responseData );
                
                // Return error condition if edges does not exist
                return {
                    data: [],
                    success: false,
                    errorMessage: 'An unknown error has occured.'
                };
            }
            
        } catch ( error ) {
            console.error( 'Error loading CMX translations', error );
                
            // Return error condition
            return { 
                data: [],
                success: false,
                errorMessage: 'An unknown error has occured.'
            };
        }    
    }

    /**
     * Creates a request using the given options. Note that the user's access
     * token is already being pulled from the Auth Service and is injected into
     * the request headers if available. If the Auth Service can't provide a
     * valid access token at the time of the request, your request may fail.
     * 
     * @param { object } options
     */
    async fetch( { endpoint, method = 'GET', headers, body, bearerToken = undefined } ) {
        return await this._fetchAssistant.fetch( { endpoint, method, headers, body, bearerToken } );
    }

    attachProgressTranslationBundle() {
        const progressNamespaceReg = /progress\.([\w-]+)\.([\w-]+)(.?)([\w-]+)?/;

        function checkSet ( path ) {
            if ( path === undefined ) {
                return {};
            } else {
                return path;
            }
        }

        function reformatLanguageBundle( bundle ) {
            const rebundle = { translation: {} };
            Object.entries( bundle )
                .filter( ( [ k, v ] ) => k.match( progressNamespaceReg ) != null )
                .forEach( ( [ k, v ] ) => {
                    const [ , namespace, key, , subKey ] = k.match( progressNamespaceReg );

                    rebundle.translation[ namespace ] = checkSet( rebundle.translation[ namespace ] );

                    if ( subKey ) {
                        rebundle.translation[ namespace ][ key ] = checkSet( rebundle.translation[ namespace ][ key ] );
                        rebundle.translation[ namespace ][ key ][ subKey ] = v;
                    } else {
                        rebundle.translation[ namespace ][ key ] = v;
                    }
                } );
            return rebundle;
        }

        const bundle = this.localTranslationBundles[ 0 ]; // will need rework if we ever have another project bundle

        const key = bundle.projectName; 

        const windowBundle = {};

        Object.entries( bundle.bundles )
            .map( ( [ k, v ] ) => [ k, reformatLanguageBundle( v ) ] )
            .forEach( ( [ k, v ] ) => windowBundle[ k ] = v );

        window[ key ] = windowBundle;
        
        return key;
    }

    /**
     * Sets a new project key to use while attempting to fetch language bundles.
     * If the provided project key is considered to be invalid, the previously
     * set project key will remain active.
     * 
     * This method will not automatically retrieve a new language bundle. Call
     * `fetchTranslations()` to retrieve and cache a new language bundle.
     * 
     * @param { string } key - The new project key to set.
     * @returns { boolean }
     */
    setProjectKey( key ) {
        if ( key ) {
            this.projectKey = key;
            return true;
        } else {
            console.warn( `Cannot set invalid project key '${ key }'.` );
            return false;
        }
    }

    /**
     * Sets a new locale code to use while attempting to fetch language bundles.
     * If the provided locale code is considered to be invalid, the previously
     * set locale code will remain active.
     * 
     * This method will not automatically retrieve a new language bundle. Call
     * `fetchTranslations()` to retrieve and cache a new language bundle, or use
     * `updateLocale(...)` to automatically fetch new translations afterwards.
     * 
     * @param { string } localeCode - The locale code to use in translations.
     * @returns { boolean }
     */
    setLocale( localeCode ) {
        if ( localeCode ) {
            this.localeCode = localeCode;
            this.notify( localeCode );
            return true;
        } else {
            console.warn( 'Failed to set locale code.' );
            return false;
        }
    }

    getLocale() {
        return this.localeCode;
    }
    
    /**
     * Sets a new locale code to use while attempting to fetch language bundles.
     * If the provided locale code is considered to be invalid, the previously
     * set locale code will remain active.
     * 
     * This method will automatically retrieve a new language bundle.
     * 
     * This method will return `true` if it was successful and will return `false`
     * if not.
     * 
     * @param { string } localeCode - The locale code to use in translations.
     * @returns { boolean }
     */
    updateLocale( localeCode ) {
        if ( !localeCode || localeCode === this.localCode ) {return true;}
        if ( this.setLocale( localeCode ) ) {
            return this.fetchTranslations( { locale: localeCode } );
        }
        return false;
    }

    /**
     * Attempts to fetch a language bundle using the currently set project key
     * and locale code. If either one are considered to be invalid, no new
     * language bundles will be retrieved.
     * 
     * Initially tries to retrieve a language bundle from CMX, but if that fails
     * for any reason, then local translations will be attempted.
     * 
     * If a new locale code is provided to this function, the controller's locale
     * code will be updated (if valid) and the translations fetched will be for
     * that new language.
     * 
     * This method will return `true` if it was successful and will return `false`
     * if not.
     * 
     * @returns { boolean }
     */
    async fetchTranslations( { locale, attemptExternal = true } = {} ) {

        const fetchExternalTranslations = async () => {
            const accessToken = authService.getAccessToken();
            if ( accessToken ) {
                return await this.fetchCmxTranslationBundle( accessToken, locale );
            }
            return {};
        };

        const fetchInternalTranslations = () => {
            if ( this.localTranslationBundles ) {
                const projectBundle = this.localTranslationBundles.find( b => b.projectName == this.projectKey ) ?? {};
                return projectBundle?.bundles?.[ this.localeCode ] ?? {};
            }
            return {};
        };

        const mergeBundles = ( posteriorityBundle, priorityBundle ) => {
            const bundle = { ...posteriorityBundle, ...priorityBundle };
            Object.entries( bundle ).forEach( ( k, v ) => {
                if ( !v ) {
                    bundle[ k ] = priorityBundle[ k ] || posteriorityBundle?.[ k ] || '';
                }
            } );
            return bundle;
        };

        const localeCode = this.localeCode;
        if ( localeCode ) {
            const successfullyUpdatedLocale = this.setLocale( localeCode );
            if ( !successfullyUpdatedLocale ) {
                this._eventSystem.invoke( this.events.FAIL, this.localeCode, undefined );
                return false;
            }
        }

        // Validate data required to fetch translations.
        const isValidProjectKey = Boolean( this.projectKey );
        const isValidCode = Boolean( this.localeCode );

        if ( isValidCode && isValidProjectKey ) {

            // Attempt to reach out to CMX and attempt to retrieve language bundle.
            const externalBundle = await fetchExternalTranslations();

            // If an external language bundle could not be retrieved, attempt to
            // grab a local language bundle.
            const internalBundle = fetchInternalTranslations();

            // merge bundles - prioritize external translations
            const languageBundle = mergeBundles( externalBundle, internalBundle );

            // If a valid language bundle was found, store it.
            if ( languageBundle ) {
                this.languageBundle = languageBundle;

                // With `intl-tools`, we have to cache and store a language bundle
                // with the locale code at the same time.
                setAndCacheLanguageBundle( this.localeCode, this.languageBundle );

                this._eventSystem.invoke( this.events.SUCCESS, this.localeCode, this.languageBundle );
                this._eventSystem.invoke( this.events.UPDATE, this.localeCode, this.languageBundle );

                return true;
            } else {
                console.warn( `No language bundle found with locale code '${ this.localeCode }' for project '${ this.projectKey }'.` );
            }
        } else {
            // Output warning message to alert the user that the translations
            // could not be retrieved.
            if ( isValidProjectKey ) {
                console.warn( 'A valid project key must be set before attempting to fetch translations.' );
            } else if ( !isValidCode ) {
                console.warn( `Could not fetch translations for invalid locale code '${ this.localeCode }'.` );
            } else {
                console.warn( 'Failed to fetch translations.' );
            }
        }

        this._eventSystem.invoke( this.events.FAIL, this.localeCode, undefined );
        this._eventSystem.invoke( this.events.UPDATE, this.localeCode, undefined );

        return false;
    }

    async getLocales() {
        await this.fetchCmxLocales()
        .then( ( res ) => {
            if ( res.ok ) {
                return res.json().then( ( data ) => {
                    const requestedBundle = ( data.success ) ? data.bundles[ this.localeCode ] : undefined;
                    if ( requestedBundle ) {
                        // ...
                    } else {
                        console.warn( 'Could not find the requested translation bundle in the CMX response...' );
                    }
                } );
            }
        } )
        .catch ( ( error ) => {
            console.error( 'Failed to fetch translation bundle from CMX.', error );
        } );
    }

    /**
     * Attempts to retrieve a translation bundle from CMX.
     * 
     * @param { string } accessToken - User access token.
     * @param { string } localeCode - The locale code to retrieve.
     */
    async fetchCmxTranslationBundle( accessToken, localeCode ) {
        const data = await this.getLanguageBundle( accessToken );
        return data.data.bundles[ localeCode ];
    }

    /**
     * Attempts to retrieve the list of locales currently supported by CMX.
     */
    async fetchCmxLocales() {
        return await this._fetchAssistant.GET( {
            endpoint: Endpoint.locales.build( CMX_ENV ),
            headers: { 'content-type': 'application/json' },
        } );
    }

    /**
     * Registers a callback under an event.
     * 
     * @param { string } eventKey - The key of the event to register the callback under.
     * @param { function } callback - The callback to register under the event.
     */
    on( eventKey, callback ) {
        this._eventSystem.addEventListener( eventKey, callback );
    }

    /**
     * Unregisters a callback from an event.
     * 
     * @param { string } eventKey - The key of the event to register the callback under.
     * @param { function } callback - The callback to unregister from the event.
     */
    off( eventKey, callback ) {
        this._eventSystem.removeEventListener( eventKey, callback );
    }

    invokeManualUpdate( loc ) {
        this._eventSystem.invoke( this.events.UPDATE, loc, undefined );
        this._eventSystem.invoke( this.events.MANUAL_UPDATE, loc, undefined );
    }

}
